/*
 * Pure helpers for the plans and billing screens. Kept free of React so the
 * rules (which state a user is in, what a price reads, how a balance prints)
 * are unit-tested on their own — every number the screen shows comes from the
 * server through these, never from a constant here.
 */
import type { BillingOverview } from '~/lib/api/billing';
import type { AccountPlan, AccountPlanOption, PlanInterval, PlanApp } from '~/lib/api/plans';

/**
 * A credit amount as the website shows it: a whole number, rounded down
 * ("158.7" → "158"), so a balance never reads higher than what's there.
 * Dunea Code and Work show the same balance to one decimal; the website keeps
 * whole credits. The tenth is settled first so a stream value like 7.9999
 * still reads "8".
 */
export function formatCredits(value: number): string {
  const tenth = Math.round((Number(value) || 0) * 10) / 10;

  return Math.floor(tenth).toLocaleString('en-US', { maximumFractionDigits: 0 });
}

/** A money amount in its currency; whole amounts without cents ("$15"), others with ("$12.50"). */
export function formatMoney(value: number, currency: string): string {
  const code = (currency || 'USD').toUpperCase();

  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: code,
      minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
      maximumFractionDigits: Number.isInteger(value) ? 0 : 2,
    }).format(value);
  } catch {
    // Intl throws on an unknown code; a pricing screen must never white-screen on one
    return `${code} ${value.toLocaleString('en-US')}`;
  }
}

export interface PlanPrice {
  value: number;
  currency: string;
}

/**
 * What a plan costs per period at this cadence, exactly as the server sells
 * it: the buyer's local price when Dodo has one (what checkout will charge),
 * else the USD price. A yearly price is the plan's own yearly price — never a
 * monthly price × 10, since Dodo rounds each product on its own. Null when the
 * plan has no price at that cadence (Free yearly).
 */
export function planPrice(plan: AccountPlanOption, interval: PlanInterval): PlanPrice | null {
  if (plan.tier === 'free') {
    return { value: 0, currency: 'USD' };
  }

  if (interval === 'year') {
    if (plan.localPriceYear) {
      return plan.localPriceYear;
    }

    return plan.priceUsdYear != null ? { value: plan.priceUsdYear, currency: 'USD' } : null;
  }

  return plan.localPrice ?? { value: plan.priceUsd, currency: 'USD' };
}

/**
 * The yearly saving as a whole percent, from the server's own USD prices
 * (Plus: $150 a year vs 12 × $15 = 17%). Null unless every paid plan saves the
 * same, so a single "Save N%" label is never wrong for one of them.
 */
export function annualSavingsPercent(plans: AccountPlanOption[]): number | null {
  // plans sold yearly only: Max is monthly only and has no yearly price to compare
  const paid = plans.filter((p) => p.tier !== 'free' && p.priceUsdYear != null);

  if (paid.length === 0) {
    return null;
  }

  const percents = paid.map((p) =>
    p.priceUsdYear != null && p.priceUsd > 0 ? Math.round((1 - p.priceUsdYear / (12 * p.priceUsd)) * 100) : null,
  );

  const [first] = percents;

  return first != null && first > 0 && percents.every((x) => x === first) ? first : null;
}

/** The plans to show at a cadence: every plan monthly; yearly, only those sold yearly (Plus and Pro — not Free or Max). */
export function plansAtInterval(plans: AccountPlanOption[], interval: PlanInterval): AccountPlanOption[] {
  return interval === 'year' ? plans.filter((p) => p.tier !== 'free' && p.priceUsdYear != null) : plans;
}

const PAID_RANK: Record<string, number> = { plus: 1, pro: 2, max: 3 };

/** Whether moving from one paid tier to another is a move up (Plus → Pro → Max). */
export function isHigherTier(target: string, current: string): boolean {
  return (PAID_RANK[target] ?? 0) > (PAID_RANK[current] ?? 0);
}

/**
 * Which billing screen a user gets:
 *  - 'legacy': a website plan (Starter/Growth/Pro/Scale) still running, or a
 *    failed website renewal being retried. No longer sold; kept until they cancel.
 *  - 'plan':   Plus or Pro, bought here, in Dunea Desktop or in Dunea Work.
 *  - 'free':   no plan.
 */
export type PlanScreen = 'legacy' | 'plan' | 'free';

export function planScreenOf(overview: Pick<BillingOverview, 'tier' | 'status' | 'subscriptionId' | 'accountPlan'>): {
  screen: PlanScreen;
  app: PlanApp;
} {
  const webRetrying =
    Boolean(overview.subscriptionId) && (overview.status === 'past_due' || overview.status === 'on_hold');

  if (overview.tier !== 'free' || webRetrying || overview.accountPlan?.product === 'web') {
    return { screen: 'legacy', app: 'code' };
  }

  const product = overview.accountPlan?.product;

  if (product === 'code' || product === 'work') {
    return { screen: 'plan', app: product === 'work' ? 'work' : 'code' };
  }

  return { screen: 'free', app: 'code' };
}

/** What a grandfathered website plan is called. */
export const LEGACY_PLAN_NAMES: Record<string, string> = {
  starter: 'Starter',
  growth: 'Growth',
  pro: 'Pro',
  scale: 'Scale',
};

/** Where the plan behind the balance was bought, for the plan card's subtitle. */
export const PLAN_PRODUCT_LABEL: Record<NonNullable<AccountPlan['product']>, string> = {
  web: 'Dunea website',
  code: 'Dunea',
  work: 'Dunea Work',
};

/**
 * True when the account has a paid plan in any product — what unlocks the
 * website's paid features (the server's billing/plan-access.ts rule).
 */
export function hasPaidPlan(overview: Pick<BillingOverview, 'tier' | 'accountPlan'> | undefined): boolean {
  if (!overview) {
    return false;
  }

  return overview.tier !== 'free' || (overview.accountPlan?.product ?? null) !== null;
}
